import React, { useState, useEffect } from 'react';
import { ArrowRight, Coffee, Check, Sparkles } from 'lucide-react';
import { RoastType, CoffeeType } from './types';
import OrderForm from '../Order/OrderForm';
import OrderSuccess from '../Order/OrderSuccess';
import { supabase } from '../../lib/supabase';

interface ResultScreenProps {
  roastType: CoffeeType;
  email: string;
  skippedQuiz?: boolean;
  isMarblehead?: boolean;
  selectedTier?: any;
  purchaseType?: 'monthly' | 'single';
  selectedRoast?: string;
  onReset: () => void;
  onClose: () => void;
}

interface SubscriptionOption {
  id: string;
  months: number;
  monthly_price: number | null;
  upfront_price: number | null;
  single_price: number | null;
  bags: number;
  size_oz: number;
  label: string;
  savings_text: string;
  popular: boolean;
  active: boolean;
  is_subscription: boolean;
}

const roastInfo = {
  light: {
    emoji: '🕊️',
    title: 'Light Roast Type',
    description:
      "Bright, zippy, citrusy. Like drinking sunshine. You're a light roast fan. Welcome to the snobbiest corner of coffee… in the best way.",
    color: 'from-yellow-400/20 to-orange-300/20'
  },
  medium: {
    emoji: '🌿',
    title: 'Medium Roast Type',
    description:
      "Smooth, balanced, crowd-pleasing. You're basically the Switzerland of coffee drinkers. Medium roast is your sweet spot.",
    color: 'from-amber-400/20 to-orange-400/20'
  },
  dark: {
    emoji: '🌋',
    title: 'Dark Roast Type',
    description:
      'Rich, smoky, bold. You like your coffee like your humor: a little dark and slightly bitter. Dark roast has entered the chat.',
    color: 'from-orange-600/20 to-red-600/20'
  },
  espresso: {
    emoji: '🚀',
    title: 'Espresso Type',
    description:
      "Intense, rich, and concentrated. You're not here to sip—you're here to *feel*. Espresso is your spirit animal.",
    color: 'from-red-500/20 to-purple-600/20'
  }
};

export default function ResultScreen({
  roastType,
  email,
  skippedQuiz = false,
  isMarblehead = false,
  selectedTier,
  purchaseType: initialPurchaseType = 'monthly',
  selectedRoast,
  onReset,
  onClose
}: ResultScreenProps) {
  const [options, setOptions] = useState<SubscriptionOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SubscriptionOption | null>(null);
  const [selectedRoastType, setSelectedRoastType] = useState<CoffeeType>(selectedRoast || roastType);
  const [purchaseType, setPurchaseType] = useState<'monthly' | 'single'>(initialPurchaseType);
  const [selectedBags, setSelectedBags] = useState(1);
  const [deliveryFrequency, setDeliveryFrequency] = useState<'all-at-once' | 'spread-out'>('all-at-once');
  const [currentStep, setCurrentStep] = useState<'selection' | 'order' | 'success'>('selection');
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  
  const selectedRoastInfo = React.useMemo(() => {
    // Handle specific roast IDs
    if (selectedRoastType === 'regiment-roast') {
      return {
        emoji: '⚓',
        title: 'Regiment Roast',
        description: 'Community. Craft. Showing up. A full-bodied medium roast meant for early mornings, shared tables, and the quiet moments before the day gets moving.',
        color: 'from-amber-700/20 to-amber-500/20'
      };
    }
    if (selectedRoastType === 'wake-with-drake') {
      return {
        emoji: '🏈',
        title: 'Wake with Drake',
        description: 'Bold, smooth, and dependable — the kind of cup you want before kickoff, school drop-off, or game day.',
        color: 'from-red-500/20 to-blue-500/20'
      };
    }
    if (selectedRoastType === 'barnegat-breakfast') {
      return {
        emoji: '🌅',
        title: 'Barnegat Breakfast',
        description: 'Our signature morning blend - smooth, balanced, and perfect for starting your day right.',
        color: 'from-amber-400/20 to-orange-400/20'
      };
    }
    if (selectedRoastType === 'soleless-shakedown') {
      return {
        emoji: '⚡',
        title: 'Soleless Shakedown',
        description: 'Bold and mischievous - a coffee that means business and isn\'t afraid to show it.',
        color: 'from-orange-600/20 to-red-600/20'
      };
    }
    if (selectedRoastType === 'nunnepog-fog') {
      return {
        emoji: '🌫️',
        title: 'Nunnepog Fog Espresso Blend',
        description: 'Designed for espresso lovers, perfect for any brew. The world can wait. Pour the quiet.',
        color: 'from-slate-400/20 to-blue-400/20'
      };
    }
    // Fall back to generic roast types
    return roastInfo[selectedRoastType as RoastType] || roastInfo.medium;
  }, [selectedRoastType]);

  const specificRoasts = [
    {
      id: 'regiment-roast',
      name: 'Regiment Roast',
      description: 'Community. Craft. Showing up.',
      emoji: '⚓',
      available: true
    },
    {
      id: 'wake-with-drake',
      name: 'Wake with Drake',
      description: 'For football mornings and family traditions',
      emoji: '🏈',
      available: false
    },
    {
      id: 'barnegat-breakfast',
      name: 'Barnegat Breakfast',
      description: 'Our signature morning blend',
      emoji: '🌅',
      available: true
    },
    {
      id: 'soleless-shakedown',
      name: 'Soleless Shakedown',
      description: 'Bold and mischievous',
      emoji: '⚡',
      available: true
    },
    {
      id: 'nunnepog-fog',
      name: 'Nunnepog Fog Espresso Blend',
      description: 'Designed for espresso lovers, perfect for all methods',
      emoji: '🌫️',
      available: true
    }
  ];

  useEffect(() => {
    loadSubscriptionOptions();
  }, []);

  const loadSubscriptionOptions = async () => {
    // Create a simple option for both monthly and single purchases
    const simpleOption = {
      id: 'simple-option',
      months: 1,
      monthly_price: 2200, // $22.00
      upfront_price: null,
      single_price: 2200, // $22.00
      bags: 1,
      size_oz: 12,
      label: 'Coffee Delivery',
      savings_text: '',
      popular: false,
      active: true,
      is_subscription: true
    };
    
    setOptions([simpleOption]);
    setSelectedOption(simpleOption);
    setLoading(false);
  };

  const handleOrderSuccess = (name: string, isMarblehead: boolean, selectedTier: any) => {
    setCustomerName(name);
    setCurrentStep('success');
  };

  const handleProceedToOrder = () => {
    if (!selectedOption) return;

    // Check if the selected roast is available
    const roast = specificRoasts.find(r => r.id === selectedRoastType);
    if (purchaseType === 'single' && !roast?.available) {
      // Don't proceed if roast is sold out
      return;
    }

    // For non-Marblehead single orders, go directly to Stripe
    if (purchaseType === 'single' && !isMarblehead) {
      // Get the payment link directly
      const getPaymentLink = () => {
        if (selectedRoastType === 'regiment-roast') {
          return 'https://buy.stripe.com/aFaaEY6TBdllfwpabU3VC0j';
        } else if (selectedRoastType === 'wake-with-drake') {
          return 'https://buy.stripe.com/fZu14o7XF9552JD3Nw3VC0h';
        } else if (selectedRoastType === 'barnegat-breakfast') {
          return 'https://buy.stripe.com/6oUcN691Jdll6ZT97Q3VC03';
        } else if (selectedRoastType === 'soleless-shakedown') {
          return 'https://buy.stripe.com/8x24gA91Jftt5VPck23VC02';
        } else if (selectedRoastType === 'nunnepog-fog') {
          return 'https://buy.stripe.com/eVqfZigub3KL6ZT0Bk3VC09';
        }
        return 'https://buy.stripe.com/6oUcN691Jdll6ZT97Q3VC03'; // fallback
      };

      const paymentLink = getPaymentLink();
      window.open(paymentLink, '_blank');

      // Show success after delay
      setTimeout(() => {
        setCurrentStep('success');
      }, 2000);
    } else {
      setCurrentStep('order');
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#fda36b] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-white/80">Loading your coffee options...</p>
      </div>
    );
  }

  if (currentStep === 'order' && selectedOption) {
    return (
      <OrderForm
        roastType={selectedRoastType}
        purchaseType={purchaseType}
        selectedOption={selectedOption}
        email={email}
        selectedBags={selectedBags}
        quizSkipped={skippedQuiz}
        isMarblehead={isMarblehead}
        onSuccess={handleOrderSuccess}
        onBack={() => setCurrentStep('selection')}
      />
    );
  }

  if (currentStep === 'success') {
    return (
      <OrderSuccess
        purchaseType={purchaseType}
        roastType={selectedRoastType}
        customerName={customerName}
        isMarblehead={isMarblehead}
        selectedTier={selectedOption}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Result Header */}
      <div className="text-center space-y-6">
        <div className="space-y-4">
          <div className="text-6xl mb-4">☕</div>
          <h2 className="text-3xl font-bold text-white">
            Perfect! Let's Get You Some Great Coffee
          </h2>
        </div>
      </div>

      {/* Purchase Type Selection */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-white text-center">
          {purchaseType === 'monthly' ? 'Monthly Delivery Selected' : 'Single Order Selected'}
        </h3>
        
        <p className="text-lg text-white/80 text-center mb-6">
          {purchaseType === 'monthly' 
            ? "Perfect! We'll send you a variety of our roasts each month to keep things interesting. Cancel anytime, no commitment."
            : "Great choice! You've selected a single order - choose your specific roast below."
          }
        </p>
        
        {/* Explanation */}
        <div className="bg-white/5 p-4 rounded-lg border border-white/10 max-w-2xl mx-auto">
          <p className="text-white/80 text-sm text-center leading-relaxed">
            {purchaseType === 'monthly' 
              ? "You'll get to try all our different roasts over time - we'll curate the selection for you each month! Cancel anytime, no strings attached."
              : "Select your specific blend below. Perfect for trying something new or getting more of a favorite."
            }
          </p>
        </div>
      </div>

      {/* Roast Selection - Only show for single purchases */}
      {purchaseType === 'single' && !selectedRoast && (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-white text-center">
            Choose Your Blend
          </h3>
          
          <div className="grid gap-3 max-w-md mx-auto">
            {specificRoasts.map((roast) => (
              <div
                key={roast.id}
                className="space-y-2"
              >
                <button
                  onClick={() => roast.available && setSelectedRoastType(roast.id as CoffeeType)}
                  disabled={!roast.available}
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                    selectedRoastType === roast.id
                      ? 'border-[#fda36b] bg-[#fda36b]/10'
                      : roast.available
                      ? 'border-white/20 bg-white/5 hover:border-white/40'
                      : 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{roast.emoji}</div>
                    <div className="flex-1">
                      <div className="text-white font-semibold">
                        {roast.name}
                        {!roast.available && (
                          <span className="ml-2 text-sm font-normal text-red-400 bg-red-500/20 px-2 py-1 rounded">
                            Sold Out
                          </span>
                        )}
                      </div>
                      <div className="text-white/70 text-sm">
                        {roast.description}
                      </div>
                    </div>
                  </div>
                </button>
                <div className="text-center">
                  {roast.available ? (
                    <a
                      href={`/rateyouroast/${roast.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#fda36b] hover:text-[#fda36b]/80 text-sm underline transition-colors"
                    >
                      Learn more about {roast.name} →
                    </a>
                  ) : (
                    <p className="text-red-400/70 text-sm">
                      Currently unavailable
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show selected roast for single purchases */}
      {purchaseType === 'single' && selectedRoast && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-lg border border-[#fda36b]/20 max-w-2xl mx-auto">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-[#fda36b]">
                Your Selected Roast
              </h3>
              <div className="flex items-center justify-center gap-4">
                <div className="text-4xl">
                  {selectedRoast === 'regiment-roast' ? '⚓' :
                   selectedRoast === 'wake-with-drake' ? '🏈' :
                   selectedRoast === 'barnegat-breakfast' ? '🌅' :
                   selectedRoast === 'nunnepog-fog' ? '🌫️' : '⚡'}
                </div>
                <div className="text-left">
                  <div className="text-xl font-bold text-white">
                    {selectedRoast === 'regiment-roast' ? 'Regiment Roast' :
                     selectedRoast === 'wake-with-drake' ? 'Wake with Drake' :
                     selectedRoast === 'barnegat-breakfast' ? 'Barnegat Breakfast' :
                     selectedRoast === 'nunnepog-fog' ? 'Nunnepog Fog Espresso Blend' :
                     'Soleless Shakedown'}
                  </div>
                  <div className="text-white/80">
                    {selectedRoast === 'regiment-roast'
                      ? 'Community. Craft. Showing up.'
                      : selectedRoast === 'wake-with-drake'
                      ? 'Bold, smooth, and dependable'
                      : selectedRoast === 'barnegat-breakfast'
                      ? 'Our signature morning blend'
                      : selectedRoast === 'nunnepog-fog'
                      ? 'Designed for espresso lovers'
                      : 'Bold and mischievous'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary */}
      {options.length > 0 && selectedOption && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-lg border border-[#fda36b]/20 max-w-2xl mx-auto">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-bold text-[#fda36b]">
                Your Order Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Selection:</span>
                  <span className="text-white font-semibold">
                    {purchaseType === 'monthly'
                      ? 'Monthly Variety'
                      : selectedRoast === 'regiment-roast' ? 'Regiment Roast' :
                        selectedRoast === 'wake-with-drake' ? 'Wake with Drake' :
                        selectedRoast === 'barnegat-breakfast' ? 'Barnegat Breakfast' :
                        selectedRoast === 'soleless-shakedown' ? 'Soleless Shakedown' :
                        selectedRoast === 'nunnepog-fog' ? 'Nunnepog Fog' :
                        selectedRoastType === 'regiment-roast' ? 'Regiment Roast' :
                        selectedRoastType === 'wake-with-drake' ? 'Wake with Drake' :
                        selectedRoastType === 'barnegat-breakfast' ? 'Barnegat Breakfast' :
                        selectedRoastType === 'soleless-shakedown' ? 'Soleless Shakedown' :
                        selectedRoastType === 'nunnepog-fog' ? 'Nunnepog Fog' :
                        'Selected Blend'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Type:</span>
                  <span className="text-white font-semibold">
                    {purchaseType === 'monthly' ? 'Monthly Subscription' : 'Single Order'}
                  </span>
                </div>
                <div className="border-t border-white/20 pt-3">
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-[#fda36b] font-bold">
                      Starting at:
                    </span>
                    <span className="text-[#fda36b] font-bold">
                      $27.00
                      {purchaseType === 'monthly' ? '/month' : ''}
                    </span>
                  </div>
                  <p className="text-white/70 text-sm mt-2 text-center">
                    Choose quantity during checkout
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
        <button
          onClick={onReset}
          className="bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300"
        >
          Start Over
        </button>

        <button
          onClick={handleProceedToOrder}
          disabled={
            (purchaseType === 'single' && !selectedRoast && !specificRoasts.find(r => r.id === selectedRoastType)?.available) ||
            (purchaseType === 'single' && !specificRoasts.find(r => r.id === selectedRoastType)?.available)
          }
          className="bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(purchaseType === 'single' && !specificRoasts.find(r => r.id === selectedRoastType)?.available)
            ? 'Sold Out - Select Another Roast'
            : purchaseType === 'single' && !isMarblehead
            ? 'Proceed to Secure Checkout'
            : 'Order My Coffee'
          }
          {!(purchaseType === 'single' && !specificRoasts.find(r => r.id === selectedRoastType)?.available) && (
            <ArrowRight className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Fun Footer */}
      <div className="bg-white/5 p-6 rounded-lg border border-white/10 text-center">
        <p className="text-white/80 leading-relaxed">
          <span className="font-semibold text-[#fda36b]">Questions?</span> We're here to help! 
          Email us at{' '}
          <a 
            href="mailto:hello@thebondcoffee.com" 
            className="text-[#fda36b] hover:text-[#fda36b]/80 underline"
          >
            hello@thebondcoffee.com
          </a>
          {' '}or just reply to any of our emails. We actually read them!
        </p>
      </div>
    </div>
  );
}